import { observer } from 'mobx-react-lite';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import errorIcon from '/assets/images/error-icon.png';
import './DragAndDropUploader.scss';

interface DragAndDropUploaderProps extends PropsWithChildren {
  elementId: string;
  handleDroppedFile: (event: FileList) => void;
  isUploadedFile?: boolean;
  className?: string;
  errorMessages?: string[];
  renderLabel?: string | React.JSX.Element;
  disabled?: boolean;
}

const DragAndDropUploader: React.FC<DragAndDropUploaderProps> = (props) => {
  const {
    elementId,
    handleDroppedFile,
    isUploadedFile = false,
    className = '',
    children,
    errorMessages,
    renderLabel,
    disabled,
  } = props;

  const [isOnDragging, setIsOnDragging] = useState(false);

  const handleDragIn = (event: DragEvent) => {
    event.preventDefault();
    setIsOnDragging(true);
  };

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault();
    setIsOnDragging(false);
  };

  const handleDropped = (event: DragEvent) => {
    event.preventDefault();
    handleDroppedFile(event.dataTransfer?.files as FileList);
    setIsOnDragging(false);
  };

  useEffect(() => {
    const droppableElement = document.getElementById(elementId);
    droppableElement?.addEventListener('dragover', handleDragIn);
    droppableElement?.addEventListener('dragleave', handleDragLeave);
    droppableElement?.addEventListener('drop', handleDropped);
    return () => {
      droppableElement?.removeEventListener('dragover', handleDragIn);
      droppableElement?.removeEventListener('dragleave', handleDragLeave);
      droppableElement?.removeEventListener('drop', handleDropped);
    };
  });

  useEffect(() => {
    isUploadedFile && setIsOnDragging(false);
  }, [isUploadedFile]);

  const uploaderAreaClassName = () => (
    `uploader-area ${(isUploadedFile || disabled) ? 'disabled' : ''} ${isOnDragging ? 'onDragging' : ''}`
  );

  return (
    <div
      className={`drag-and-drop-uploader ${className}`}
    >
      {
        renderLabel && (
          <span
            className='label'
          >
            {renderLabel}
          </span>
        )
      }
      <div
        id={elementId}
        className={uploaderAreaClassName()}
      >
        <div
          className='uploader-area-inner'
        >

          {children}
        </div>
      </div>
      {
        errorMessages && (
          <div>
            {
              errorMessages.map((errorMessage, index) => (
                <div
                  key={index}
                  className='error-uploading-file'
                >
                  <img
                    src={errorIcon}
                    alt='info icon'
                  />
                  <span
                    className='text-status-error'
                  >
                    {errorMessage}
                  </span>
                </div>
              ))
            }
          </div>
        )
      }
    </div>
  );
};

export default observer(DragAndDropUploader);
